import { FC } from 'react';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

import { StageEntryDto } from 'src/generated/services/TFinancialApi';
import StatusChip from 'src/presentations/components/organisms/dashboard/StatusChip';

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), 'dd-MMM-yyyy hh.mm aa zzz');
  } catch {
    return dateStr;
  }
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), 'dd-MMM-yyyy');
  } catch {
    return dateStr;
  }
};

type StageVariant = 'verification' | 'done' | 'default';

const getStageVariant = (entry: StageEntryDto): StageVariant => {
  const s = `${entry.status || ''} ${entry.actionCategory || ''}`.toUpperCase();
  if (s.includes('APPROVED')) return 'done';
  if (s.includes('REJECTED') || s.includes('REJECT')) return 'default';
  return 'verification';
};

const SingleStageIcon: FC<{ variant: StageVariant }> = ({ variant }) => {
  if (variant === 'done') {
    return (
      <div className='w-36 h-36 rounded-8 bg-[#CCEBD5] flex items-center justify-center flex-shrink-0'>
        <CheckCircleIcon sx={{ color: '#00992B', fontSize: 20 }} />
      </div>
    );
  }
  if (variant === 'default') {
    return (
      <div className='w-36 h-36 rounded-8 bg-[#FCE4E9] flex items-center justify-center flex-shrink-0'>
        <InfoIcon sx={{ color: '#E72547', fontSize: 20 }} />
      </div>
    );
  }
  return (
    <div className='w-36 h-36 rounded-8 bg-[#FDEAD3] flex items-center justify-center flex-shrink-0'>
      <CheckCircleIcon sx={{ color: '#F79824', fontSize: 20 }} />
    </div>
  );
};

const beautifyStageName = (stage: string) =>
  stage
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

interface SingleStageItemProps {
  stage: string;
  entry: StageEntryDto;
}

export const SingleStageItem: FC<SingleStageItemProps> = ({ stage, entry }) => {
  const variant = getStageVariant(entry);
  const subtitle = `by ${entry.by || entry.from || ''}${entry.to ? ` to ${entry.to}` : ''}`;

  return (
    <div className='relative pb-32'>
      <div className='absolute left-18 top-2 w-1 bottom-2 bg-neutral-20' />
      <div className='flex gap-16 relative z-10'>
        <SingleStageIcon variant={variant} />
        <div className='flex-1 min-w-0'>
          <div className='flex items-start justify-between gap-8'>
            <div className='min-w-0'>
              <h3 className='text-14 font-semibold text-neutral-90'>{beautifyStageName(stage)}</h3>
              <p className='text-12 text-blue_grey-30 mt-2'>{subtitle}</p>
              <div className='mt-8'>
                <StatusChip status={entry?.status} size='small' />
              </div>
            </div>
            {entry.completedAt && (
              <div className='text-right flex-shrink-0'>
                <p className='text-12 text-neutral-70 font-medium whitespace-nowrap'>{formatDate(entry.completedAt)}</p>
                <p className='text-12 text-neutral-50 whitespace-nowrap mt-1'>{formatDateTime(entry.completedAt)?.split(' ').slice(1).join(' ')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
